<template>
    <div v-if="week" class="table-scroll" id="table-scroll-day">
        <table class="v-datatable v-table theme--light">
            <thead>
                <tr>
                    <th style="min-width: 200px; padding: 4px;" align="center">
                        <v-text-field
                            style="width: 200px;"
                            label="Location Name"
                            :value="localFilterLocationName"
                            @input="handleModifiedFilterLocationNameChanged"
                            prepend-inner-icon="search"
                            single-line
                            hide-details
                            dense
                            outline>
                        </v-text-field>
                    </th>
                    <th style="min-width: 150px;" class="pa-0">Variety</th>
                    <th class="pa-0"></th>
                    <th class="dateNavigator pa-0">
                        <v-btn outline fab small @click="incrementWeek(-1)"><v-icon>chevron_left</v-icon></v-btn>
                    </th>
                    <th class="info lighten-2 font-weight-bold text-uppercase pa-0" 
                        style="min-width: 100px;">
                        <div class="vertical-cell">
                            <v-layout align-center justify-center row fill-height>
                                <div class="vertical-cell-left">
                                    <v-checkbox tabindex="-1" label="TY" class="pr-2" v-if="!week.locked && showYieldValues && week.yieldedCrops.length > 0" 
                                        :value="forecastWeeksYieldApplied.filter(f=> f.week == week.week).length == week.yieldedCrops.length && week.yieldedCrops.length != 0" 
                                        hide-details 
                                        v-on:click.stop.prevent="applyWeekYield(week)"></v-checkbox>
                                    <v-checkbox tabindex="-1" label="P" class="pr-2" v-if="!week.locked && showProfileValues && week.profiledCrops.length > 0" 
                                        :value="forecastWeeksProfileApplied.filter(f=> f.week == week.week).length == week.profiledCrops.length && week.profiledCrops.length != 0" 
                                        hide-details 
                                        v-on:click.stop.prevent="applyWeekProfile(week)"></v-checkbox>
                                </div>
                                <span>WK{{ week.financialWeek }}</span>
                            </v-layout>
                         </div>
                    </th>
                    <th v-for="(day, index) in week.days" 
                        :key="index" 
                        :class="`${index < 7 ? `grey` : 'blue-grey'} ${day.isToday ? `lighten-2 font-weight-bold` : `lighten-4`} pa-0`"
                        style="min-width: 100px;">
                        <v-badge v-model="day.publicHoliday" color="red" overlap>
                            <template v-slot:badge>
                                <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-icon small light v-on="on">event</v-icon>
                                </template>
                                <span>{{day.holidayName}}</span>
                                </v-tooltip>
                            </template>
                            <div class="vertical-cell">
                                <span :class="`${day.isToday ? `text-uppercase` : ``}`">{{ momentCache.getMoment(day.day).format('ddd') }}</span>
                                <span>{{ momentCache.getMoment(day.day).format('DD/MM/YY') }}</span>
                            </div>
                        </v-badge>
                    </th>
                    <th class="dateNavigator pa-0">
                        <v-btn outline fab small @click="incrementWeek(1)"><v-icon>chevron_right</v-icon></v-btn>
                    </th>
                </tr>
            </thead>
            <tbody v-if="calendarSortedItems.length == 0">
                <tr>
                    <td :colspan="6 + week.days.length"></td>
                </tr>
            </tbody>
            <InfiniteLoadingTableBody v-else :data="calendarSortedItems" :page-size="12">
                <template v-slot:rows="{ shownData }">
                    <ForecastingOverviewCalendarDayRow
                        v-for="(item, index) in shownData" 
                        :key="index"
                        :item="item"
                        :forecastWeeksYieldApplied="forecastWeeksYieldApplied"
                        :forecastWeeksProfileApplied="forecastWeeksProfileApplied"
                        @onYieldsApplied="$emit('onYieldsApplied', $event)"
                        @onProfilesApplied="$emit('onProfilesApplied', $event)"
                        @goToDetail="goToDetail(item)">
                    </ForecastingOverviewCalendarDayRow>
                </template>
                <template v-slot:footerRows>
                    <tr class="fillerRow">
                        <td v-for="i in 4 +  week.days.length" :key="i"></td>
                    </tr>
                </template>
            </InfiniteLoadingTableBody>
            <tfoot>
                <tr>
                    <td class="text-xs-center pa-0" colspan="2">
                        <v-layout row wrap>
                            <v-flex xs12>
                                <span>Total Active Locations in selection:</span>
                                <span class="ml-1 font-weight-bold">{{ calendarNumberOfLocationsFiltered }}</span>
                            </v-flex>
                            <v-flex xs12 v-if="showKG">
                                <span class="caption font-italic">Values shown in kilograms (kg)</span>
                            </v-flex>
                            <v-flex xs12 v-else>
                                <span class="caption font-italic">Values shown in trays</span>
                            </v-flex>
                            <v-flex xs12>
                                <span class="caption" ><span class="error--text" style="font-weight:bold;font-size:15;">!</span> indicates forecast volume against spray clash</span>
                            </v-flex>
                        </v-layout>
                    </td>
                    <td colspan="2">
                        <v-layout column align-end>
                            <v-flex xs12>
                                <span>Total:</span>
                            </v-flex>
                            <v-flex xs12>
                                <span>ICA Total:</span>
                            </v-flex>
                            <v-flex xs12>
                                <span>Actual Total:</span>
                            </v-flex>                          
                        </v-layout>
                    </td>
                    <td class="info lighten-2 font-weight-bold pa-0">
                        <div class="vertical-cell">
                            <v-layout column align-center>
                                <v-flex xs12>
                                    <span> 
                                        <v-icon v-if="!week.locked && editable" color="primary" @click="openBulkUpdateDialogForWeek(week.week)">more_vert</v-icon>
                                        <v-icon v-if="week.locked">lock_outline</v-icon>
                                        {{ calculateWeekTotal(week.week) }}
                                    </span>
                                </v-flex>
                                <v-flex xs12>
                                    <span>                                        
                                        {{calculateICAWeekTotal(week.week)}}                                        
                                    </span>
                                </v-flex>
                                <v-flex xs12>
                                    <span>                                        
                                        {{calculateActualWeekTotal(week.week)}}                                        
                                    </span>
                                </v-flex>                            
                            </v-layout>
                        </div>
                    </td>
                    <td v-for="(day, index) in week.days" 
                            :key="index" 
                            class="pa-0"
                            style="min-width: 100px;"
                            >
                            <div class="vertical-cell">
                                <v-layout column align-center>
                                    <v-flex xs12>
                                        <span> 
                                            <v-icon v-if="(!day.forecastLocked || !day.adjustedForecastLocked) && editable" color="primary" @click="openBulkUpdateDialogForDay(day.day)">more_vert</v-icon>
                                            <v-icon v-if="day.forecastLocked && day.adjustedForecastLocked">lock_outline</v-icon>
                                                {{ calculateDayTotal(day.day) }}
                                        </span>
                                    </v-flex>
                                    <v-flex xs12>
                                        <span>                                        
                                            {{ calculateICADayTotal(day.day) }}                                        
                                        </span>
                                    </v-flex>
                                    <v-flex xs12>
                                        <span>                                        
                                            {{ calculateActualDayTotal(day.day) }}                                        
                                        </span>
                                    </v-flex>
                                </v-layout>
                            </div>
                        </td>
                    <td></td>
                </tr>
            </tfoot>
        </table>
        <ForecastingOverviewCalendarBulkUpdateDialog 
            :dialog="dialog" 
            :selectedDate="selectedBulkUpdateDate"
            :rules="[validateForecastValue]"
            :isWeekMode="isWeekMode"
            :originalGrandTotal="selectedGrandTotal"
            :originalTotalSquareMeters="selectedTotalSquareMeters"
            @close="closeBulkUpdateDialog">
        </ForecastingOverviewCalendarBulkUpdateDialog>
    </div>
</template>

<script>
import ForecastingOverviewCalendarDayRow from '@/components/forecasting/ForecastingOverviewCalendarDayRow.vue'
import ForecastingOverviewCalendarBulkUpdateDialog from '@/components/forecasting/ForecastingOverviewCalendarBulkUpdateDialog.vue'
import InfiniteLoadingTableBody from '@/components/generic/InfiniteLoadingTableBody.vue';
import * as Constants from '@/helpers/constants';

import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import moment from "moment";
import momentCache from "@/services/momentCache";

export default {
    components: {
        ForecastingOverviewCalendarDayRow,
        ForecastingOverviewCalendarBulkUpdateDialog,
        InfiniteLoadingTableBody
    },
    props: {
        forecastWeeksYieldApplied: {
            type: Array,
            required: true
        },
        forecastWeeksProfileApplied: {
            type: Array,
            required: true
        }
    },
    data(){
        return {
            momentCache,
            moment,
            dialog: false,
            selectedBulkUpdateDate: "",
            isWeekMode: false,
            selectedGrandTotal: 0,
            selectedTotalSquareMeters: 0,
            modifiedFilterLocationName: null,
            filterLocationNameTimeout: null
        };
    },
    computed: {
        ...mapState("ForecastingStore", [
            "selectedDate",
            "filterLocationName",
            "detailedWeeks",
            "calendarSortedItems",
            "calendarNumberOfLocationsFiltered",
            "calendarWeekTotals",
            "calendarDayTotals",
            "dayViewScrollPosition",
            "showYieldValues",
            "showProfileValues",
            "selectedUOM"
        ]),
        ...mapGetters("ForecastingStore", [
            "startOfSelectedWeek",
            "editable"
        ]),
        week(){
            return this.detailedWeeks.find(x => x.week == this.startOfSelectedWeek);
        },
        localFilterLocationName(){
            if(this.modifiedFilterLocationName != null){
                return this.modifiedFilterLocationName;
            }

            return this.filterLocationName;
        },
        showKG() {
            return this.selectedUOM === Constants.HARVESTPLAN_UOM_ENUM.KG.ID
        }
    },
    methods: {
        ...mapMutations("ForecastingStore", [
            "setFilterLocationName",
            "setDayViewScrollPosition"
        ]),
        ...mapActions("ForecastingStore", [
            "updateData"
        ]),
        handleModifiedFilterLocationNameChanged(filterLocationName){
            this.modifiedFilterLocationName = filterLocationName;

            if(this.filterLocationNameTimeout != null){
                clearTimeout(this.filterLocationNameTimeout);
            }

            this.filterLocationNameTimeout = setTimeout(this.handleFilterLocationNameChanged, 200);
        },
        async handleFilterLocationNameChanged(){
            this.filterLocationNameTimeout = null;
            this.setFilterLocationName(this.modifiedFilterLocationName);
            await this.updateData();
            this.modifiedFilterLocationName = null;
        },
        goToDetail(item){
            this.$emit('goToDetail', {
                cropLocationId: item.cropLocationId,
                varietyId: item.varietyId
            });
        },
        calculateWeekTotal(week){
            let weekValue = this.calendarWeekTotals.find(x => x.week == week);
            if(weekValue == null){
                return 0;
            }

            return weekValue.total;
        },
        calculateICAWeekTotal(week){
            let weekValue = this.calendarWeekTotals.find(x => x.week == week);
            if(weekValue == null){
                return 0;
            }

            return weekValue.icaTotal;
        },
        calculateActualWeekTotal(week){
            let weekValue = this.calendarWeekTotals.find(x => x.week == week);
            if(weekValue == null){
                return 0;
            }

            return weekValue.actualTotal;
        },
        calculateDayTotal(day) {
           let dayValue = this.calendarDayTotals.find(x => x.day == day);
            if(dayValue == null){
                return 0;
            }

            return dayValue.total;
        },
        calculateICADayTotal(day) {
           let dayValue = this.calendarDayTotals.find(x => x.day == day);
            if(dayValue == null){
                return 0;
            }

            return dayValue.icaTotal;
        },
        calculateActualDayTotal(day) {
           let dayValue = this.calendarDayTotals.find(x => x.day == day);
            if(dayValue == null){
                return 0;
            }

            return dayValue.actualTotal;
        },
        calculateTotalSquareMeters(){
            return this.calendarSortedItems.reduce((a, v) => a + v.cropArea, 0);
        },
        openBulkUpdateDialogForDay(day){
            this.selectedBulkUpdateDate = day;
            this.isWeekMode = false;
            this.selectedGrandTotal = this.calculateDayTotal(day);
            this.selectedTotalSquareMeters = this.calculateTotalSquareMeters();

            this.dialog = true;
        },
        openBulkUpdateDialogForWeek(week){
            this.selectedBulkUpdateDate = week;
            this.isWeekMode = true;
            this.selectedGrandTotal = this.calculateWeekTotal(week);
            this.selectedTotalSquareMeters = this.calculateTotalSquareMeters();

            this.dialog = true;
        },
        closeBulkUpdateDialog() {
            this.dialog = false;
        },
        validateForecastValue(v){
            if(v == null || !v.match(/^[0-9]{1,5}$/)){
                return "Please enter a number in the form XXXXX";
            }

            return false;
        },
        applyWeekYield(week) {
            this.$emit("applyWeekYield", week);
        },
        applyWeekProfile(week) {
            this.$emit("applyWeekProfile", week);
        },
        async incrementWeek(value){       
            this.$emit('IncrementDate', value);
        },
    },
    created(){
        
    },
    mounted() {
        
    }
}
</script>

<style scoped>
.dateNavigator {
    max-width:75px;
}
.dateNavigator i {
    line-height: 2
}
.vertical-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.vertical-cell-left {
    display: flex;
    flex-direction: column;    
}

.table-scroll {
    position: absolute;
    z-index: 1;
    overflow: auto;
    top: 0px;
    bottom: 0px;
    left: 0px;
    right: 0px;
    min-height: 100px;
}

.table-scroll table {
    width: 100%;
    height: 100%;
    margin: auto;
}

.table-scroll table tr td, .table-scroll table tr th {
    width: auto;
}

.table-scroll thead tr td, .table-scroll thead tr th {
    position: -webkit-sticky;
    position: sticky;
    background-color: white;
    z-index: 5;
}

.table-scroll thead tr:nth-child(1) td, .table-scroll thead tr:nth-child(1) th {
    top: 0px;
    height: 70px;
}

.table-scroll tfoot tr td, .table-scroll tfoot tr th {
    position: -webkit-sticky;
    position: sticky;
    background-color: white;
    z-index: 25;
}

.table-scroll tfoot tr:nth-child(1) td, .table-scroll tfoot tr:nth-child(1) th {
    bottom: 0px;
    height: 70px;
}

.table-scroll tfoot td:nth-child(1) {
    z-index: 25;
}

 .table-scroll th:nth-child(1) {
    position: -webkit-sticky;
    position: sticky;
    left: 0;
    z-index: 25;
}

.table-scroll .fillerRow th, .fillerRow td {
    height: auto;
}

table thead th {
    font-size: 16px;;
}
</style>